/**
 * The Client deactivate confirmation (f6.1), driven entirely by the server.
 *
 * The prototype's `modal narrow` with `alert alert-warning` carrying `a-title`
 * and `a-body`, plus its Reason textarea.
 *
 * **The client never computes the child list.** The sequence is f4.1's, and
 * this is the third feature to use it:
 *
 * 1. The dialog opens as a plain confirmation.
 * 2. Confirming issues the deactivation **without** `acknowledge_active_children`.
 * 3. If the server refuses with `409 ACTIVE_CHILDREN_PRESENT`, the dialog stays
 *    open and renders the children **it** named.
 * 4. Confirming again re-issues the same request with the acknowledgement.
 *
 * A Client has no registered children until f7.1 adds `Engagement`, so step 3
 * never fires today. That is exactly why the flow is built this way now: the
 * prototype's dialog already promises the f7.1 behaviour -- *"6 active
 * engagements below this client ... They stay attached and keep their
 * history"* -- and because this dialog renders **whatever the server names**,
 * it will start listing engagements with **no change to this file**. An
 * engagement created between steps 2 and 4 makes step 4 refuse again rather
 * than succeed silently.
 */

import { useState } from 'react';
import { Ban, TriangleAlert } from 'lucide-react';

import type { Client } from '@/api/client';
import { useDeactivateClient } from '@/api/queries';
import { Modal } from '@/routes/reference-data/Modal';

import { activeChildrenFrom, generalMessage } from './clientView';

export function DeactivateClientDialog({
  client,
  onClose,
}: {
  client: Client;
  onClose: () => void;
}) {
  const deactivate = useDeactivateClient();
  const [reason, setReason] = useState('');

  const activeChildren = activeChildrenFrom(deactivate.error);
  /*
   * A refusal that named children is the expected answer to step 2, not a
   * failure to repeat at the top of the dialog.
   */
  const failure = activeChildren ? null : generalMessage(deactivate.error);

  function submit() {
    deactivate.mutate(
      {
        clientId: client.id,
        body: {
          // False on the first attempt, true once the warning has been seen.
          acknowledge_active_children: activeChildren !== null,
          ...(reason.trim() ? { reason: reason.trim() } : {}),
        },
      },
      { onSuccess: onClose },
    );
  }

  return (
    <Modal
      title="Deactivate client?"
      subtitle={client.name}
      onClose={onClose}
      onSubmit={submit}
      busy={deactivate.isPending}
      submitLabel={activeChildren ? 'Deactivate anyway' : 'Deactivate'}
      submitIcon={<Ban aria-hidden="true" />}
      submitClassName="btn btn-danger"
      narrow
      layout="plain"
      message={failure}
    >
      <div className="alert alert-warning mb-4" role="alert">
        <TriangleAlert aria-hidden="true" />
        <div>
          <div className="a-title">
            {activeChildren && activeChildren.total > 0
              ? `${activeChildren.total} active ${
                  activeChildren.total === 1 ? 'record' : 'records'
                } below this client`
              : `${client.name} stops being offered for new records`}
          </div>
          <div className="a-body">
            They stay attached and keep their history. Nothing is deleted, no child is
            cascaded, and the client can be reactivated.
          </div>
          {activeChildren && activeChildren.listed.length > 0 && (
            <>
              <ul className="group-children">
                {activeChildren.listed.map((entry) => (
                  <li key={entry.id}>
                    <span className="mono">{entry.code}</span> {entry.name}
                  </li>
                ))}
              </ul>
              {activeChildren.total > activeChildren.listed.length && (
                <div className="a-body">
                  and {activeChildren.total - activeChildren.listed.length} more.
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <div className="field">
        <label htmlFor="cl-deact-reason">Reason</label>
        <textarea
          id="cl-deact-reason"
          value={reason}
          placeholder="Recorded on the audit entry"
          onChange={(event) => setReason(event.target.value)}
        />
      </div>
    </Modal>
  );
}
